import { isProjectStatus, ProjectStatus } from './project-status';
import { Job } from './job';
import { NoteDto } from './note';
import { ProjectAttachment } from './project-attachment';
import { ProjectType } from './project-type';
import { DelegationShareSubTenant } from './role';

export interface ExternalId {
  id: string;
}

export type ExternalIdMap = { [key: string]: ExternalId };

export interface RelayProject {
  tenant: string;
  id: string;
  message: any;
  updated_on?: string;
  updated?: number;
  expires?: number; // Used for dynamo auto expiry
}

export enum UserSource {
  RELAY = 'Relay',
  PATHWAY = 'Pathway'
}

type Owner = string;

export interface TasksWorkflowState {
  id: string;
  state: any;
}

export type Module = 'sip';

interface SipModuleFailed {
  status: 'FAILED';
}

interface SipModuleSent {
  status: 'SENT';
  jobId: string;
}

type SipModule = SipModuleSent | SipModuleFailed;

interface DnoModule {
  status: string;
  isLegacy?: boolean;
}

interface ProjectModules {
  sip?: SipModule;
  dno?: DnoModule;
}

export interface ProductPackages {
  packages: ProductPackage[];
}

interface ProductPackage {
  type: ProductPackageTypes;
  options: ProductPackageOptions[];
}

export type ProductPackageTypes = 'Installation' | 'Hardware' | 'Accessories';

export type ProjectFlagType = 'priority' | 'escalation' | 'support';

export interface ProjectFlag {
  type: ProjectFlagType;
  reason?: string;
  comment?: string;
  active: boolean;
}

export interface ProjectFlagDto extends ProjectFlag {
  userLabel: string;
  userId: string;
  updatedDateTime: string;
  tenant: string;
  subTenant: { id: string; name: string };
}

interface ProductPackageOptions {
  name: string;
  default?: boolean;
}

export interface Project {
  tenant: string;
  type: ProjectType;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  productPackages?: ProductPackages;
  id?: string;
  data?: any;
  jobs?: Job[];
  status?: ProjectStatus;
  tasksWorkflow?: any;
  tasksWorkflowStates?: TasksWorkflowState[];
  resources?: any[];
  attachments?: ProjectAttachment[];
  notes?: NoteDto[];
  created_on?: string;
  created_by?: UserSource;
  updated_on?: string;
  updated_by?: UserSource;
  owner?: Owner;
  owner_name?: Owner;
  created_by_name?: string;
  created_by_team?: string;
  created_by_team_name?: string;
  created_by_roles?: string[];
  relaySubmissionDate?: string;
  externalIds?: ExternalIdMap;
  delegationChain?: string;
  documentPackId?: string;
  autoAssignment?: ProjectAutoAssignment;
  modules?: ProjectModules;
  tenantType?: string;

  primaryOwner?: string;
  created_by_teams?: string[];
  subTenants?: DelegationShareSubTenant[];
  subTenantId?: string;
  assigned_to_dates?: { from: string; to: string; date: string }[];
  assigned_to_team?: string;
  assigned_to_team_name?: string;
  isAggregator?: boolean;
  scheduleNotes?: string;
  flags?: ProjectFlagDto[];
  statusUpdatedOn?: string;
}

export const isProject = (project: unknown): project is Project => {
  return (
    typeof project === 'object' &&
    project !== null &&
    'id' in project &&
    typeof project.id === 'string' &&
    'tenant' in project &&
    typeof project.tenant === 'string' &&
    'type' in project &&
    typeof project.type === 'string' &&
    'status' in project &&
    isProjectStatus(project.status)
  );
};

export interface ProjectAutoAssignment {
  disabled: boolean;
}
